/**
 * Hub Profile — the saved CP profile, the code, and the published agreement.
 *
 * Edits apply immediately (no re-review) and send only the fields that
 * changed. The CP code, the owning account and the agreement acceptance are
 * not editable here.
 */
import * as React from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { Eye, Pencil, ScrollText, UserRound } from 'lucide-react';
import { formatDate, panTypeLabel } from '@finmin/utils';
import {
  Button,
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
  ErrorMessage,
  Form,
  Skeleton,
  Spinner,
  toast,
} from '@finmin/ui';
import {
  useChannelPartnerContext,
  useChannelPartnerOptions,
  useUpdateChannelPartnerProfile,
  type ChannelPartnerContext,
  type ChannelPartnerOptions,
  type ChannelPartnerProfile,
} from '@/api/channel-partners';
import {
  OTHERS_PARTNER_TYPE,
  buildProfileUpdate,
  profileDefaults,
  profileFormSchema,
  type ProfileFormInput,
} from '../application-schema';
import { openChannelPartnerDocument } from '../open-document';
import { AgreementSummary } from './agreement-summary';
import { ChannelPartnerCodeCard } from './code-card';
import { ChannelPartnerFieldset } from './profile-fieldset';

export function ChannelPartnerHubProfile() {
  const me = useChannelPartnerContext();
  const options = useChannelPartnerOptions();
  const [editing, setEditing] = React.useState(false);
  const profile = me.data?.profile;

  if (!me.data || !profile || options.isLoading) {
    return (
      <div className="space-y-4">
        <Skeleton className="h-64 w-full" />
        <Skeleton className="h-40 w-full" />
      </div>
    );
  }

  return (
    <div className="grid grid-cols-1 gap-6 lg:grid-cols-[minmax(0,1fr)_380px] lg:items-start">
      <div className="min-w-0 space-y-6">
        <ErrorMessage message={options.error?.message ?? null} />
        {editing && options.data ? (
          <ProfileEditForm profile={profile} options={options.data} onDone={() => setEditing(false)} />
        ) : (
          <ProfileDetails
            profile={profile}
            options={options.data}
            onEdit={options.data ? () => setEditing(true) : undefined}
          />
        )}
      </div>
      <aside className="min-w-0 space-y-6">
        <ChannelPartnerCodeCard code={profile.cp_code} />
        <AgreementCard context={me.data} options={options.data} />
        <AccountCard context={me.data} />
      </aside>
    </div>
  );
}

function Row({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="grid grid-cols-1 gap-1 py-3 sm:grid-cols-[200px_minmax(0,1fr)] sm:gap-4">
      <dt className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">{label}</dt>
      <dd className="min-w-0 break-words text-sm text-foreground">{children || '—'}</dd>
    </div>
  );
}

function DocumentLink({ id }: { id: string | null }) {
  if (!id) return <span className="text-muted-foreground">Not added</span>;
  return (
    <Button variant="link" size="sm" className="h-auto p-0" onClick={() => void openChannelPartnerDocument(id)}>
      <Eye className="h-3.5 w-3.5" aria-hidden /> View file
    </Button>
  );
}

function ProfileDetails({
  profile,
  options,
  onEdit,
}: {
  profile: ChannelPartnerProfile;
  options: ChannelPartnerOptions | undefined;
  onEdit?: () => void;
}) {
  const typeLabel =
    options?.partner_types.find((t) => t.id === profile.partner_type)?.label ?? profile.partner_type;
  return (
    <Card>
      <CardHeader className="flex flex-row items-start justify-between gap-3 space-y-0">
        <div>
          <CardTitle className="text-base">Channel Partner profile</CardTitle>
          <CardDescription>
            Approved {formatDate(profile.approved_at)}. Changes apply immediately.
          </CardDescription>
        </div>
        {onEdit && (
          <Button variant="outline" size="sm" onClick={onEdit}>
            <Pencil className="h-3.5 w-3.5" aria-hidden /> Edit
          </Button>
        )}
      </CardHeader>
      <CardContent>
        <dl className="divide-y divide-border">
          <Row label="Channel Partner email">{profile.cp_email}</Row>
          <Row label="Partner type">
            {typeLabel}
            {profile.partner_type === OTHERS_PARTNER_TYPE && profile.other_partner_type
              ? ` — ${profile.other_partner_type}`
              : ''}
          </Row>
          <Row label="Organisation">{profile.organisation_name}</Row>
          <Row label="Location">{[profile.city, profile.state].filter(Boolean).join(', ')}</Row>
          <Row label="Heard about FinminOS">{profile.acquisition_source}</Row>
          <Row label="Goals">
            {profile.goals ? <span className="whitespace-pre-line">{profile.goals}</span> : null}
          </Row>
          <Row label="PAN">
            {profile.pan ? (
              <span>
                <span className="font-mono tracking-wider">{profile.pan}</span>
                {profile.pan_type && (
                  <span className="text-muted-foreground"> · {panTypeLabel(profile.pan_type)}</span>
                )}
              </span>
            ) : null}
          </Row>
          <Row label="PAN card">
            <DocumentLink id={profile.pan_document_id} />
          </Row>
          <Row label="GSTIN">
            {profile.gstin ? <span className="font-mono tracking-wider">{profile.gstin}</span> : null}
          </Row>
          <Row label="GST certificate">
            <DocumentLink id={profile.gst_document_id} />
          </Row>
        </dl>
      </CardContent>
    </Card>
  );
}

function ProfileEditForm({
  profile,
  options,
  onDone,
}: {
  profile: ChannelPartnerProfile;
  options: ChannelPartnerOptions;
  onDone: () => void;
}) {
  const update = useUpdateChannelPartnerProfile();
  const [apiError, setApiError] = React.useState<string | null>(null);
  const form = useForm<ProfileFormInput>({
    resolver: zodResolver(profileFormSchema),
    defaultValues: profileDefaults(profile, options),
    mode: 'onTouched',
  });

  function onSubmit(values: ProfileFormInput) {
    const changes = buildProfileUpdate(profile, values);
    if (Object.keys(changes).length === 0) {
      toast({ title: 'Nothing to save', description: 'Your profile already has these details.' });
      onDone();
      return;
    }
    setApiError(null);
    update.mutate(changes, {
      onSuccess: () => {
        toast.success('Profile updated');
        onDone();
      },
      onError: (err) => setApiError(err.message || 'Could not save your profile. Please try again.'),
    });
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-base">Edit Channel Partner profile</CardTitle>
        <CardDescription>Required fields must stay filled in.</CardDescription>
      </CardHeader>
      <CardContent>
        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} noValidate className="space-y-8">
            <ChannelPartnerFieldset options={options} disabled={update.isPending} />
            <ErrorMessage message={apiError} />
            <div className="flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
              <Button type="button" variant="outline" onClick={onDone} disabled={update.isPending}>
                Cancel
              </Button>
              <Button type="submit" disabled={update.isPending}>
                {update.isPending ? (
                  <>
                    <Spinner className="h-4 w-4" /> Saving…
                  </>
                ) : (
                  'Save changes'
                )}
              </Button>
            </div>
          </form>
        </Form>
      </CardContent>
    </Card>
  );
}

function AgreementCard({
  context,
  options,
}: {
  context: ChannelPartnerContext;
  options: ChannelPartnerOptions | undefined;
}) {
  const current = options?.agreement ?? null;
  const accepted = context.application?.status === 'approved' ? context.application : null;
  const newerPublished = Boolean(current && accepted?.agreement_id && accepted.agreement_id !== current.id);
  return (
    <Card>
      <CardHeader className="pb-3">
        <CardTitle className="flex items-center gap-2 text-base">
          <ScrollText className="h-4 w-4 text-muted-foreground" aria-hidden />
          Channel Partner Agreement
        </CardTitle>
        {accepted?.accepted_at && (
          <CardDescription>You accepted the agreement on {formatDate(accepted.accepted_at)}.</CardDescription>
        )}
      </CardHeader>
      <CardContent className="space-y-3">
        {current ? (
          <AgreementSummary agreement={current} />
        ) : (
          <p className="text-sm text-muted-foreground">No agreement is published right now.</p>
        )}
        {newerPublished && (
          <p className="text-xs text-muted-foreground">
            A newer version has been published since you joined. Your approval continues — there is
            nothing to accept again.
          </p>
        )}
      </CardContent>
    </Card>
  );
}

function AccountCard({ context }: { context: ChannelPartnerContext }) {
  const { account } = context;
  return (
    <Card>
      <CardHeader className="pb-3">
        <CardTitle className="flex items-center gap-2 text-base">
          <UserRound className="h-4 w-4 text-muted-foreground" aria-hidden />
          Your FinminOS account
        </CardTitle>
        <CardDescription>The Channel Partner Hub is part of this {account.role} account.</CardDescription>
      </CardHeader>
      <CardContent>
        <dl className="space-y-2 text-sm">
          <div className="flex justify-between gap-3">
            <dt className="text-muted-foreground">Name</dt>
            <dd className="truncate text-foreground">{account.name || '—'}</dd>
          </div>
          <div className="flex justify-between gap-3">
            <dt className="text-muted-foreground">Mobile</dt>
            <dd className="text-foreground">{account.mobile}</dd>
          </div>
          <div className="flex justify-between gap-3">
            <dt className="text-muted-foreground">Account email</dt>
            <dd className="truncate text-foreground">{account.email || '—'}</dd>
          </div>
        </dl>
      </CardContent>
    </Card>
  );
}
